import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Plus,
  Search,
  MessageSquare,
  Image as ImageIcon,
  FileText,
  Layout,
  List,
  CheckCircle,
  Clock,
  Edit,
  Trash2,
  Eye,
  Loader,
  AlertCircle,
  RefreshCw,
  XCircle,
  Pause,
  Copy,
  CloudDownload,
  StopCircle,
  Ban,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  getTemplates,
  deleteTemplate,
  updateTemplate,
  refreshTemplateStatus,
  getTemplateById,
  syncTemplatesFromMeta,
} from "../apis/template";
import { usePhoneAccount } from "../contexts/PhoneAccountContext";
import ViewEditTemplateModal from "../components/ViewEditTemplateModal";

const Templates = () => {
  const { selectedPhoneId } = usePhoneAccount();
  const [userData, setUserData] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterLanguage, setFilterLanguage] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");
  const [isLoading, setIsLoading] = useState(true);
  const [counts, setCounts] = useState({});
  const [error, setError] = useState("");
  const [refreshingId, setRefreshingId] = useState(null);
  const [duplicatingId, setDuplicatingId] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncSuccess, setSyncSuccess] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalTemplates, setTotalTemplates] = useState(0);

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    const user = localStorage.getItem("user");
    if (user) {
      const parsed = JSON.parse(user);
      setUserData(parsed);
    }
  }, []);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterStatus, filterLanguage, filterCategory, searchTerm]);

  // Fetch templates when page, items per page, filters, or phoneId change
  useEffect(() => {
    fetchTemplates(currentPage, itemsPerPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, filterStatus, filterLanguage, filterCategory, searchTerm, selectedPhoneId]);

  // Helper function to extract display data from template for table view
  const getTemplateDisplayData = (template) => {
    const templateJson = template.template_json || {};
    const components = templateJson.components || [];
    
    // Extract basic info for display
    const bodyComponent = components.find(c => c.type === 'BODY');
    const headerComponent = components.find(c => c.type === 'HEADER');
    const buttonsComponent = components.find(c => c.type === 'BUTTONS');
    
    const body = bodyComponent?.text || '';
    const headerType = headerComponent?.format?.toLowerCase() || 'none';
    const rawButtons = buttonsComponent?.buttons || [];
    
    // Determine template type for icon display
    let templateType = 'text';
    if (rawButtons.length > 0) {
      templateType = 'buttons';
    } else if (headerType !== 'none' && headerType !== 'text') {
      templateType = headerType;
    }
    
    return {
      ...template,
      body,
      template_type: templateType,
      header_type: headerType,
      meta_template_id: template.template_id || template.meta_template_id,
    };
  };

  const fetchTemplates = async (page = currentPage, perPage = itemsPerPage) => {
    setIsLoading(true);
    setError("");
    try {
      const authToken = localStorage.getItem("userAccessToken");
      
      // Calculate offset based on page number
      const offset = (page - 1) * perPage;
      
      // Prepare filters for backend
      const filters = {
        phoneId: selectedPhoneId || null,
        limit: perPage,
        offset,
      };
      
      // Add filters if not "all"
      if (filterStatus !== "all") {
        filters.status = filterStatus;
      }
      if (filterLanguage !== "all") {
        filters.language = filterLanguage;
      }
      if (filterCategory !== "all") {
        filters.category = filterCategory;
      }
      if (searchTerm.trim()) {
        filters.searchTerm = searchTerm.trim();
      }
      
      const response = await getTemplates(authToken, filters);
      
      // Get templates and add display data only
      const rawTemplates = response.data.metaTemplates || [];
      const templatesWithDisplay = rawTemplates.map(getTemplateDisplayData);
      
      // Get total count from backend
      const total = response.data.total || 0;
      setTotalTemplates(total);
      
      // Get counts from backend response (array format: [{status: 'APPROVED', count: 10}, ...])
      const backendCounts = response.data.counts || [];
      const countsObj = {
        approved: 0,
        pending: 0,
        rejected: 0
      };
      
      // Transform backend counts array to object format
      backendCounts.forEach((item) => {
        const status = item.status?.toLowerCase();
        if (status && countsObj.hasOwnProperty(status)) {
          countsObj[status] = item.count || 0;
        }
      });

      setTemplates(templatesWithDisplay);
      setCounts(countsObj);
    } catch (err) {
      console.error("Error fetching templates:", err);
      setError(err.response?.data?.error || "Failed to load templates");
    } finally {
      setIsLoading(false);
    }
  };

  // Refresh template status from Meta
  const handleRefreshStatus = async (template) => {
    if (!template.template_id) {
      alert("This template hasn't been submitted to Meta yet.");
      return;
    }

    setRefreshingId(template.id);
    setError("");

    try {
      const authToken = localStorage.getItem("userAccessToken");
      const response = await refreshTemplateStatus(authToken, template.id);

      if (response.data.success) {
        // Update local state
        setTemplates((prev) =>
          prev.map((t) =>
            t.id === template.id
              ? {
                  ...t,
                  status: response.data.status,
                  rejected_reason: response.data.rejectedReason || null,
                }
              : t
          )
        );

        // Show success message
        const statusMessage =
          response.data.status?.toLowerCase() === "approved"
            ? "✅ Template approved by Meta!"
            : response.data.status === "rejected"
            ? "❌ Template rejected by Meta"
            : `Status updated to: ${response.data.status}`;

        alert(statusMessage);

        // Refresh counts
        await fetchTemplates();
      }
    } catch (err) {
      console.error("Error refreshing status:", err);
      const errorMessage =
        err.response?.data?.error || "Failed to refresh template status";
      setError(errorMessage);
      alert(errorMessage);
    } finally {
      setRefreshingId(null);
    }
  };

  const handleViewEdit = async (template) => {
    // Fetch full template details for view/edit modal
    try {
      const authToken = localStorage.getItem("userAccessToken");
      const response = await getTemplateById(authToken, template.id);
      const fullTemplate = response.data.template || template;
      setSelectedTemplate(fullTemplate);
      setShowModal(true);
    } catch (err) {
      console.error("Error fetching template:", err);
      setError(err.response?.data?.error || "Failed to load template details");
    }
  };

  const handleSaveTemplate = async (templateId, updateData) => {
    try {
      const authToken = localStorage.getItem("userAccessToken");
      await updateTemplate(authToken, templateId, updateData);

      // Refresh templates list
      await fetchTemplates();

      return true;
    } catch (err) {
      console.error("Error saving template:", err);
      throw err;
    }
  };

  const handleDelete = async (templateId) => {
    if (window.confirm("Are you sure you want to delete this template?")) {
      try {
        const authToken = localStorage.getItem("userAccessToken");
        await deleteTemplate(authToken, templateId);
        setTemplates((prev) => prev.filter((t) => t.id !== templateId));
        await fetchTemplates(); // Refresh counts
      } catch (err) {
        console.error("Error deleting template:", err);
        setError(err.response?.data?.error || "Failed to delete template");
      }
    }
  };

  // Duplicate template handler
  const handleDuplicate = async (template) => {
    setDuplicatingId(template.id);
    setError("");

    try {
      const authToken = localStorage.getItem("userAccessToken");
      
      // Get full template details
      const response = await getTemplateById(authToken, template.id);
      const fullTemplate = response.data.template || template;

      // Prepare duplicate data - transformDuplicateTemplate will handle the conversion
      const duplicateData = {
        name: `copy_${fullTemplate.name}`,
        category: fullTemplate.category,
        language: fullTemplate.language,
        originalName: fullTemplate.name,
        header_media_url: fullTemplate.header_media_url || null,
        template_json: fullTemplate.template_json, // Pass template_json directly
        waba_id: fullTemplate.waba_id,
        phone_id: fullTemplate.phone_id,
      };

      // Navigate to CreateTemplate - transformDuplicateTemplate will handle the rest
      navigate("/templates/create", {
        state: {
          duplicateTemplate: duplicateData,
        },
      });
    } catch (err) {
      console.error("Error duplicating template:", err);
      const errorMessage =
        err.response?.data?.error || "Failed to duplicate template";
      setError(errorMessage);
      alert(errorMessage);
    } finally {
      setDuplicatingId(null);
    }
  };

  // Sync templates from Meta
  const handleSyncTemplates = async () => {
    const wabaAccount = JSON.parse(
      localStorage.getItem("selectedWabaAccount") || "{}"
    );
    const wabaId = wabaAccount?.wabaId;
    const phoneId = wabaAccount?.phoneId;

    if (!wabaId || !phoneId) {
      setError("Please select a WhatsApp Business Account first");
      return;
    }

    setIsSyncing(true);
    setError("");
    setSyncSuccess(null);

    try {
      const authToken = localStorage.getItem("userAccessToken");
      const response = await syncTemplatesFromMeta(authToken, wabaId, phoneId);

      if (response.data.success) {
        const stats = response.data.stats;
        setSyncSuccess({
          message: `Synced ${stats.total} templates`,
          details: `${stats.inserted} new, ${stats.updated} updated, ${stats.deleted} removed`,
          duration: stats.duration,
        });

        // Refresh templates list
        await fetchTemplates();

        // Auto-hide success message after 5 seconds
        setTimeout(() => setSyncSuccess(null), 5000);
      }
    } catch (err) {
      console.error("Error syncing templates:", err);
      const errorMessage =
        err.response?.data?.error ||
        err.response?.data?.details ||
        "Failed to sync templates from Meta";
      setError(errorMessage);
    } finally {
      setIsSyncing(false);
    }
  };

  const getTypeIcon = (type) => {
    const icons = {
      text: MessageSquare,
      image: ImageIcon,
      document: FileText,
      buttons: Layout,
      list: List,
    };
    return icons[type] || MessageSquare;
  };

  const getStatusBadge = (status) => {
    if (!status) return {
      icon: Edit,
      label: "DRAFT",
      class: "bg-gray-100 text-gray-700 border-gray-200",
    };
    
    // Normalize status: convert to string, lowercase, and trim whitespace
    const normalizedStatus = String(status).toLowerCase().trim();
    
    const configs = {
      approved: {
        icon: CheckCircle,
        label: "APPROVED",
        class: "bg-emerald-100 text-emerald-700 border-emerald-200",
      },
      pending: {
        icon: Clock,
        label: "PENDING",
        class: "bg-amber-100 text-amber-700 border-amber-200",
      },
      rejected: {
        icon: XCircle,
        label: "REJECTED",
        class: "bg-red-100 text-red-700 border-red-200",
      },
      paused: {
        icon: Pause,
        label: "PAUSED",
        class: "bg-orange-100 text-orange-700 border-orange-200",
      },
      disabled: {
        icon: Ban,
        label: "DISABLED",
        class: "bg-red-100 text-red-700 border-red-200",
      },
      draft: {
        icon: Edit,
        label: "DRAFT",
        class: "bg-gray-100 text-gray-700 border-gray-200",
      },
    };
    
    const result = configs[normalizedStatus] || configs.draft;
    return result;
  };

  const getQualityBadge = (quality) => {
    if (!quality) return {
      label: "UNKNOWN",
      class: "bg-gray-100 text-gray-700 border-gray-200",
    };
    
    const normalizedQuality = String(quality).toUpperCase().trim();
    
    const configs = {
      GREEN: {
        label: "GREEN",
        class: "bg-emerald-100 text-emerald-700 border-emerald-200",
      },
      YELLOW: {
        label: "YELLOW",
        class: "bg-amber-100 text-amber-700 border-amber-200",
      },
      RED: {
        label: "RED",
        class: "bg-red-100 text-red-700 border-red-200",
      },
      UNKNOWN: {
        label: "UNKNOWN",
        class: "bg-gray-100 text-gray-700 border-gray-200",
      },
    };
    
    return configs[normalizedQuality] || configs.UNKNOWN;
  };

  // Get unique languages, statuses, and categories from current page templates for filter dropdowns
  const uniqueLanguages = [...new Set(templates.map(t => t.language).filter(Boolean))].sort();
  const uniqueStatuses = [...new Set(templates.map(t => t.status).filter(Boolean))].sort();
  const uniqueCategories = [...new Set(templates.map(t => t.category).filter(Boolean))].sort();

  // Calculate pagination info
  const totalPages = Math.ceil(totalTemplates / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, totalTemplates);

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-[#fff]">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-5 py-4">
        {/* Header */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Templates</h1>
              <p className="text-sm text-gray-600 mt-1">Manage your WhatsApp message templates</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleSyncTemplates}
                disabled={isSyncing}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-xl transition-all shadow-sm border ${
                  isSyncing
                    ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                    : "text-gray-900 border-gray-300 hover:shadow-md"
                }`}
                style={{ backgroundColor: isSyncing ? undefined : "#e9eeea" }}
                title="Sync templates from Meta"
              >
                {isSyncing ? (
                  <>
                    <Loader size={16} className="animate-spin" />
                    <span className="hidden sm:inline">Syncing...</span>
                  </>
                ) : (
                  <>
                    <CloudDownload size={16} />
                    <span className="hidden sm:inline">Sync from Meta</span>
                  </>
                )}
              </button>
              <button
                onClick={() => navigate("/templates/create")}
                className="flex items-center gap-2 px-4 py-2.5 text-gray-900 text-sm font-semibold rounded-xl transition-all shadow-sm hover:shadow-md border border-gray-300"
                style={{ backgroundColor: "#eefa94" }}
              >
                <Plus size={16} />
                New Template
              </button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div
              className="rounded-2xl p-4 border border-gray-200 hover:shadow-sm transition-all"
              style={{ backgroundColor: "#e9eeea" }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-600 mb-1 font-medium uppercase tracking-wide">
                    Total Templates
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {totalTemplates}
                  </p>
                </div>
                <div
                  className="p-3 rounded-xl border border-gray-300"
                  style={{ backgroundColor: "#eefa94" }}
                >
                  <MessageSquare className="text-gray-900" size={20} />
                </div>
              </div>
            </div>

            <div
              className="rounded-2xl p-4 border border-gray-200 hover:shadow-sm transition-all"
              style={{ backgroundColor: "#eefa94" }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-800 mb-1 font-medium uppercase tracking-wide">Approved</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {counts.approved || 0}
                  </p>
                </div>
                <div className="p-3 bg-emerald-100 rounded-xl border border-emerald-200">
                  <CheckCircle className="text-emerald-600" size={20} />
                </div>
              </div>
            </div>

            <div
              className="rounded-2xl p-4 border border-gray-200 hover:shadow-sm transition-all"
              style={{ backgroundColor: "#e9eeea" }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-600 mb-1 font-medium uppercase tracking-wide">Pending</p>
                  <p className="text-2xl font-bold text-amber-600">
                    {counts.pending || 0}
                  </p>
                </div>
                <div className="p-3 bg-amber-100 rounded-xl border border-amber-200">
                  <Clock className="text-amber-600" size={20} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4 p-4 bg-red-50 border border-red-200 rounded-2xl flex items-center gap-3"
          >
            <AlertCircle className="text-red-600 flex-shrink-0" size={18} />
            <div className="flex-1">
              <p className="text-sm text-red-700 font-medium">{error}</p>
            </div>
            <button
              onClick={() => setError("")}
              className="text-red-600 hover:text-red-800"
            >
              <XCircle size={18} />
            </button>
          </motion.div>
        )}

        {/* Sync Success Alert */}
        {syncSuccess && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mb-4 p-4 bg-emerald-50 border border-emerald-200 rounded-2xl flex items-center gap-3"
          >
            <CheckCircle className="text-emerald-600 flex-shrink-0" size={18} />
            <div className="flex-1">
              <p className="text-sm text-emerald-700 font-medium">
                {syncSuccess.message}
              </p>
              <p className="text-xs text-emerald-600 mt-1">
                {syncSuccess.details} • Completed in {(syncSuccess.duration / 1000).toFixed(1)}s
              </p>
            </div>
            <button
              onClick={() => setSyncSuccess(null)}
              className="text-emerald-600 hover:text-emerald-800"
            >
              <XCircle size={18} />
            </button>
          </motion.div>
        )}

        {/* Filters */}
        <div
          className="rounded-2xl border border-gray-200 p-4 mb-4"
          style={{ backgroundColor: "#e9eeea" }}
        >
          <div className="flex flex-col md:flex-row gap-3">
            <div className="flex-1 relative">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
                size={18}
              />
              <input
                type="text"
                placeholder="Search templates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent bg-white"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2.5 text-sm font-medium border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent bg-white min-w-[140px]"
            >
              <option value="all">All Status</option>
              {uniqueStatuses.map((status) => (
                <option key={status} value={status}>
                  {status.toUpperCase()}
                </option>
              ))}
            </select>
            <select
              value={filterLanguage}
              onChange={(e) => setFilterLanguage(e.target.value)}
              className="px-4 py-2.5 text-sm font-medium border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent bg-white min-w-[140px]"
            >
              <option value="all">All Languages</option>
              {uniqueLanguages.map((lang) => (
                <option key={lang} value={lang}>
                  {lang.toUpperCase()}
                </option>
              ))}
            </select>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-4 py-2.5 text-sm font-medium border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent bg-white min-w-[140px]"
            >
              <option value="all">All Categories</option>
              {uniqueCategories.map((category) => (
                <option key={category} value={category}>
                  {category.charAt(0).toUpperCase() + category.slice(1)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-16">
            <div className="text-center">
              <Loader
                size={40}
                className="animate-spin text-gray-900 mx-auto mb-4"
              />
              <p className="text-sm text-gray-600 font-medium">Loading templates...</p>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && templates.length === 0 && (
          <div
            className="rounded-3xl border-2 border-dashed border-gray-300 p-12 text-center"
            style={{ backgroundColor: "#e9eeea" }}
          >
            <div className="max-w-md mx-auto">
              <div
                className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-gray-300"
                style={{ backgroundColor: "#eefa94" }}
              >
                <MessageSquare className="text-gray-900" size={28} />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                No templates yet
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                Create your first message template to start broadcasting
              </p>
              <button
                onClick={() => navigate("/templates/create")}
                className="inline-flex items-center gap-2 px-6 py-3 text-gray-900 text-sm font-semibold rounded-xl transition-all shadow-md hover:shadow-lg border border-gray-300"
                style={{ backgroundColor: "#eefa94" }}
              >
                <Plus size={18} />
                Create Your First Template
              </button>
            </div>
          </div>
        )}

        {/* Templates Table */}
        {!isLoading && templates.length > 0 && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm"
            >
              <table className="w-full">
                <thead style={{ backgroundColor: "#e9eeea" }}>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wide">
                      SR.No
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wide">
                      Template Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wide">
                      Category
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wide">
                      Language
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wide">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wide">
                      Quality
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wide">
                      Created
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wide">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {templates.map((template, index) => {
                  const Icon = getTypeIcon(template.template_type);
                  const statusConfig = getStatusBadge(template.status);
                  const StatusIcon = statusConfig.icon;
                  const qualityConfig = getQualityBadge(template.quality_score);

                  return (
                    <tr
                      key={template.id}
                      className="hover:bg-gray-50 transition"
                    >
                      <td className="px-4 py-4">
                        <span className="text-sm text-gray-700 font-medium">
                          {startIndex + index}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div>
                          <p className="text-sm font-bold text-gray-900">
                            {template.name}
                          </p>
                          {/* Show rejection reason */}
                          {template.status?.toLowerCase() === "rejected" &&
                            template.rejected_reason && (
                              <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                                <AlertCircle size={12} />
                                {template.rejected_reason}
                              </p>
                            )}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-sm text-gray-700 font-medium capitalize">
                          {template.category.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-sm text-gray-700 font-medium uppercase">
                          {template.language || 'N/A'}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <span
                          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${statusConfig.class}`}
                        >
                          <StatusIcon size={14} />
                          {statusConfig.label}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <span
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${qualityConfig.class}`}
                        >
                          {qualityConfig.label}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600 font-medium">
                        {new Date(template.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center justify-end gap-2">
                          {/* Refresh Status Button */}
                          {template.template_id &&
                            (template.status?.toLowerCase() === "pending" ||
                              template.status?.toLowerCase() === "rejected") && (
                              <button
                                onClick={() => handleRefreshStatus(template)}
                                disabled={refreshingId === template.id}
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
                                title="Refresh status from Meta"
                              >
                                <RefreshCw
                                  size={16}
                                  className={
                                    refreshingId === template.id
                                      ? "animate-spin"
                                      : ""
                                  }
                                />
                              </button>
                            )}

                          <button
                            onClick={() => handleDuplicate(template)}
                            disabled={duplicatingId === template.id}
                            className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors disabled:opacity-50"
                            title="Duplicate template"
                          >
                            {duplicatingId === template.id ? (
                              <Loader size={16} className="animate-spin" />
                            ) : (
                              <Copy size={16} />
                            )}
                          </button>
                          <button
                            onClick={() => handleViewEdit(template)}
                            className="p-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                            title="View/Edit template"
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(template.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete template"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </motion.div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4 rounded-2xl border border-gray-200 p-4 flex flex-col sm:flex-row items-center justify-between gap-4"
              style={{ backgroundColor: "#e9eeea" }}
            >
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <span>
                  Showing <span className="font-bold text-gray-900">{startIndex}</span> to{" "}
                  <span className="font-bold text-gray-900">{endIndex}</span> of{" "}
                  <span className="font-bold text-gray-900">{totalTemplates}</span> templates
                </span>
              </div>

              <div className="flex items-center gap-3">
                {/* Items per page selector */}
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-700 font-medium">Per page:</label>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="px-3 py-2 text-sm font-medium border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent bg-white"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>

                {/* Previous button */}
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`px-4 py-2 text-sm font-semibold rounded-xl transition-colors ${
                    currentPage === 1
                      ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                      : "bg-white text-gray-900 border border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  Previous
                </button>

                {/* Page numbers */}
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-4 py-2 text-sm font-semibold rounded-xl transition-colors ${
                          currentPage === pageNum
                            ? "text-gray-900 border border-gray-300"
                            : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                        }`}
                        style={{
                          backgroundColor: currentPage === pageNum ? "#eefa94" : undefined,
                        }}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                {/* Next button */}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`px-4 py-2 text-sm font-semibold rounded-xl transition-colors ${
                    currentPage === totalPages
                      ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                      : "bg-white text-gray-900 border border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  Next
                </button>
              </div>
            </motion.div>
          )}
        </>
        )}
      </div>

      {/* View Modal */}
      {selectedTemplate && (
        <ViewEditTemplateModal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            setSelectedTemplate(null);
          }}
          template={selectedTemplate}
        />
      )}
    </div>
  );
};

export default Templates;
